import { TourItinerary } from './models/tour-itinerary';
import { cloneDeep } from '@apollo/client/utilities';
import { computed, Injectable, signal } from '@angular/core';

/**
 * Service for managing interactive tours throughout the application.
 *
 * This service provides functionality to register, start, pause, skip, and navigate
 * through tour itineraries. It uses Angular signals for reactive state management
 * and integrates with the driver.js library for tour step presentation.
 *
 * @example
 * ```typescript
 * // Register and start a tour
 * const tourService = inject(TourService);
 * const itinerary = new TourItinerary('Welcome Tour', [
 *   new TourStopPoi('#target-element', 'Title', 'Description', {
 *     nextButton: { displayText: 'Next', listener: () => tourService.nextStop() }
 *   })
 * ]);
 *
 * tourService.register([itinerary]);
 * tourService.startTour();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TourService {
  /** Internal signal for managing tour itineraries */
  private readonly itinerariesSource = signal<TourItinerary[]>([]);

  /** Read-only signal containing all registered tour itineraries */
  itineraries = this.itinerariesSource.asReadonly();

  /** Internal signal for tracking the current itinerary index */
  private readonly currentItineraryIndexSource = signal(0);

  /** Read-only signal containing the current itinerary index */
  currentItineraryIndex = this.currentItineraryIndexSource.asReadonly();

  /** Computed signal returning the currently active itinerary */
  currentItinerary = computed(
    () => this.itineraries()[this.currentItineraryIndex()]
  );

  /** Internal signal for tracking if the tour has started */
  private readonly tourStartedSource = signal(false);

  /** Read-only signal indicating if the tour is currently active */
  tourStarted = this.tourStartedSource.asReadonly();

  /** Internal signal for tracking if the tour is paused */
  private readonly tourPausedSource = signal(false);

  /** Read-only signal indicating if the tour is currently paused */
  tourPaused = this.tourPausedSource.asReadonly();

  /** Signal controlling the visibility of the tour progress card */
  showTourProgressCard = signal(false);

  /**
   * Registers a collection of tour itineraries for the tour system.
   *
   * This method must be called before starting a tour. It will clone the provided
   * itineraries to prevent external modifications during tour execution.
   *
   * @param itineraries - Array of tour itineraries to register
   * @throws {Error} When called after tour has started or with empty array
   *
   * @example
   * ```typescript
   * const welcomeItinerary = new TourItinerary('Welcome', [welcomeStop]);
   * const setupItinerary = new TourItinerary('Setup', [setupStop]);
   * tourService.register([welcomeItinerary, setupItinerary]);
   * ```
   */
  register(itineraries: TourItinerary[]) {
    if (this.tourStarted()) {
      return;
    }

    if (itineraries.length === 0) {
      throw new Error('Tour Service: At least one itinerary is required');
    }

    this.itinerariesSource.set(cloneDeep(itineraries));
  }

  /**
   * Starts the tour by activating the first itinerary.
   *
   * This method will begin the tour at the first registered itinerary and
   * first stop. If the tour is already started, this method returns early.
   *
   * @throws {Error} When no itineraries are registered
   *
   * @example
   * ```typescript
   * tourService.register([itinerary]);
   * tourService.startTour(); // Begins the tour
   * ```
   */
  startTour() {
    if (this.itineraries().length === 0) {
      throw new Error(
        'Tour Service: At least one itinerary is required. Did you register?'
      );
    }

    if (this.tourStarted()) {
      return;
    }

    this.tourStartedSource.set(true);
    this.currentItinerary().activate();
    this.updateTourProgress();
  }

  /**
   * Pauses the current tour without ending it.
   *
   * This method temporarily stops the tour presentation while maintaining
   * the current position. The tour can be resumed by navigating to stops.
   *
   * @example
   * ```typescript
   * tourService.pauseTour(); // Pauses the current tour
   * ```
   */
  pauseTour() {
    if (!this.tourStarted()) {
      return;
    }

    this.tourPausedSource.set(true);
    this.currentItinerary().pause();
    this.updateTourProgress();
  }

  /**
   * Ends the tour completely and resets all state.
   *
   * This method terminates the tour, completes the current itinerary,
   * and resets all tour-related state to initial values.
   *
   * @example
   * ```typescript
   * tourService.endTour(); // Completely ends the tour
   * ```
   */
  endTour() {
    if (!this.tourStarted()) {
      return;
    }

    this.currentItinerary().complete();

    this.itinerariesSource.set([]);
    this.currentItineraryIndexSource.set(0);
    this.tourStartedSource.set(false);
    this.tourPausedSource.set(false);
    this.showTourProgressCard.set(false);
  }

  /**
   * Navigates to a specific itinerary by index.
   *
   * This method will complete or skip the current itinerary and activate
   * the itinerary at the specified index. If the target itinerary cannot
   * be activated, it will automatically proceed to the next available itinerary.
   *
   * @param index - Zero-based index of the itinerary to navigate to
   *
   * @example
   * ```typescript
   * tourService.gotoItinerary(1); // Jump to the second itinerary
   * ```
   */
  gotoItinerary(index: number) {
    this.tourPausedSource.set(false);

    if (this.currentItinerary().progress === 'active') {
      if (
        this.currentItinerary().currentStopIndex ===
        this.currentItinerary().stops.length - 1
      ) {
        this.currentItinerary().complete();
      } else {
        this.currentItinerary().skip();
      }
    }

    this.currentItineraryIndexSource.set(index);
    if (this.currentItinerary().activate()) {
      this.updateTourProgress();
    } else {
      this.nextItinerary();
    }
  }

  /**
   * Advances to the next available itinerary.
   *
   * This method finds the next inactive itinerary and navigates to it.
   * If no inactive itineraries remain, it will navigate to the last itinerary.
   *
   * @example
   * ```typescript
   * tourService.nextItinerary(); // Proceed to next itinerary
   * ```
   */
  nextItinerary() {
    if (!this.tourStarted()) {
      return;
    }

    let nextIndex = this.itineraries().length - 1;

    // Find the next inactive itinerary
    for (
      let index = this.currentItineraryIndex() + 1;
      index < this.itineraries().length;
      index += 1
    ) {
      if (this.itineraries()[index].progress === 'inactive') {
        nextIndex = index;
        break;
      }
    }

    this.gotoItinerary(nextIndex);
  }

  /**
   * Skips the current itinerary and moves to the next one.
   *
   * This method marks the current itinerary as skipped and advances
   * to the next available itinerary in the sequence.
   *
   * @example
   * ```typescript
   * tourService.skipItinerary(); // Skip current itinerary
   * ```
   */
  skipItinerary() {
    if (!this.tourStarted()) {
      return;
    }

    this.currentItinerary().skip();
    this.nextItinerary();
  }

  /**
   * Resumes a previously skipped itinerary.
   *
   * This method allows returning to and resuming an itinerary that was
   * previously skipped. It will deactivate the current itinerary first.
   *
   * @param index - Zero-based index of the skipped itinerary to resume
   *
   * @example
   * ```typescript
   * tourService.resumeSkippedItinerary(0); // Resume the first itinerary
   * ```
   */
  resumeSkippedItinerary(index: number) {
    if (
      !this.tourStarted() ||
      this.itineraries()[index]?.progress !== 'skipped'
    ) {
      return;
    }

    this.currentItinerary().deactivate();
    this.gotoItinerary(index);
  }

  /**
   * Navigates to a specific stop within the current itinerary.
   *
   * This method will jump to the specified stop index within the
   * currently active itinerary and resume the tour.
   *
   * @param index - Zero-based index of the stop to navigate to
   *
   * @example
   * ```typescript
   * tourService.gotoStop(2); // Jump to the third stop
   * ```
   */
  gotoStop(index: number) {
    if (!this.tourStarted()) {
      return;
    }

    this.tourPausedSource.set(false);
    this.currentItinerary().gotoStop(index);
  }

  /**
   * Advances to the next stop within the current itinerary.
   *
   * This method progresses to the next stop in the current itinerary
   * and resumes the tour if it was paused.
   *
   * @example
   * ```typescript
   * tourService.nextStop(); // Proceed to next stop
   * ```
   */
  nextStop() {
    if (!this.tourStarted()) {
      return;
    }

    this.tourPausedSource.set(false);
    this.currentItinerary().nextStop();
  }

  /**
   * Updates the tour progress by refreshing the itineraries signal.
   *
   * This private method triggers reactivity by creating a new array
   * reference, ensuring that computed signals and components update
   * when tour progress changes.
   *
   * @private
   */
  private updateTourProgress() {
    this.itinerariesSource.set([...this.itineraries()]);
  }
}
